import { useCallback, useEffect, useState } from 'react'
import { TADMembershipGroup, TADMembershipGroups } from '../../../../type'
import { IQDialog, Preloader } from '../../../common'
import { colors } from '@mui/material'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import { dateFormat, Format } from '../../../../utility/date-format'
import { API } from '../../../../api'
import { MembershipType } from '../../../../view/admin/ad-management/Memberships'
import { TADMembershipTemplateDetail } from '../../../../type/ad-management'

interface IProps {
  isShow: boolean
  isExternal: boolean
  type: MembershipType
  membership: string
  date: Date
  onCancel: () => void
}

export const MembershipView = ({ isShow, membership, date, onCancel, type, isExternal = false }: IProps) => {
  const [isLoading, setIsLoading] = useState(true)
  const [groups, setGroups] = useState<TADMembershipGroups | TADMembershipTemplateDetail>(
    {} as TADMembershipGroups | TADMembershipTemplateDetail,
  )

  const getMembershipGroups = useCallback(async () => {
    if (membership) {
      try {
        setIsLoading(true)
        if (type === MembershipType.Template) {
          setGroups(await API.ADManagement.membership.detailTemplate(membership))
        } else {
          setGroups(await API.ADManagement.membership.detail(membership))
        }
      } catch (error) {
        console.error('Error fetching membership groups:', error)
      } finally {
        setIsLoading(false)
      }
    }
  }, [membership])

  useEffect(() => {
    void getMembershipGroups()
  }, [getMembershipGroups])

  const renderContent = () => {
    const renderAddedAt = () => `${dateFormat(date, Format['YYYY-MM-DD'])} ${dateFormat(date, Format['HH:MM:SS'])}`

    const renderCell = (title: string, value: number | string, testId: string) => (
      <Grid
        item
        xs={6}
        sx={{ mb: 4 }}
      >
        <Typography color={colors.grey['500']}>{title}</Typography>
        <Typography
          style={{
            wordBreak: 'break-word',
          }}
          color={colors.common.black}
          data-testid={testId}
        >
          {value}
        </Typography>
      </Grid>
    )

    const renderGroups = (groups: TADMembershipGroup[]): string => groups.map(({ GroupName }) => GroupName).join(', ')
    const renderGroupsTemplate = (groupArr: { name: string; description: string }[] = []): string =>
      groupArr.map(({ name }) => name).join(', ')

    if (type === MembershipType.Template) {
      const templateGroups = groups as TADMembershipTemplateDetail
      const memberships = templateGroups.memberships || {}
      return (
        <Grid
          container
          spacing={2}
        >
          {renderCell('Template Name', templateGroups.name || membership, 'template-name')}
          {renderCell('Pharmacy', renderGroupsTemplate(memberships.Pharmacies || []), 'template-groups-pharmacy')}
          {renderCell('CE IDs', renderGroupsTemplate(memberships.CeGroups || []), 'template-groups-CE')}
          {renderCell('Qlik Streams', renderGroupsTemplate(memberships.QlikStreams || []), 'template-groups-qlik-stream')}
          {renderCell('Qlik Apps', renderGroupsTemplate(memberships.QlikApps || []), 'template-groups-qlik-app')}
          {renderCell('Omit', renderGroupsTemplate(memberships.OmitGroups || []), 'template-groups-omit')}
          {renderCell('Distributors', renderGroupsTemplate(memberships.Distributors || []), 'template-distributors')}
          {renderCell('Manufacturers', renderGroupsTemplate(memberships.Manufacturers || []), 'template-manufacturers')}
          {renderCell('Other', renderGroupsTemplate(memberships.OtherGroups || []), 'template-groups-other')}
          {renderCell('Added At', renderAddedAt(), 'added-at')}
        </Grid>
      )
    } else {
      const membershipGroups = groups as TADMembershipGroups
      return (
        <Grid
          container
          spacing={2}
        >
          {renderCell('Membership', membership, 'membership')}
          {renderCell('Description', membershipGroups.Description, 'description')}
          {renderCell('CE IDs', renderGroups(membershipGroups.MemberOf?.CeGroups || []), 'groups-CE')}
          {renderCell('Pharmacy', renderGroups(membershipGroups.MemberOf?.Pharmacies || []), 'groups-pharmacy')}
          {renderCell('Qlik Apps', renderGroups(membershipGroups.MemberOf?.QlikApps || []), 'groups-qlik-app')}
          {renderCell('Qlik Streams', renderGroups(membershipGroups.MemberOf?.QlikStreams || []), 'groups-qlik-stream')}
          {renderCell('Omit', renderGroups(membershipGroups.MemberOf?.OmitGroups || []), 'groups-omit')}
          {renderCell('Distributors', renderGroups(membershipGroups.MemberOf?.Distributors || []), 'distributors')}
          {isExternal ? null : renderCell('Manufacturers', renderGroups(membershipGroups.MemberOf?.Manufacturers || []), 'manufacturers')}
          {renderCell('Other', renderGroups(membershipGroups.MemberOf?.OtherGroups || []), 'groups-other')}
          {renderCell('Added At', renderAddedAt(), 'added-at')}
        </Grid>
      )
    }
  }

  const renderActions = () => (
    <Button
      variant='contained'
      onClick={onCancel}
      data-testid={`${type === MembershipType.Template ? 'template' : 'membership'}-ok`}
    >
      OK
    </Button>
  )

  return (
    <IQDialog
      isShow={isShow}
      size='md'
      onClose={onCancel}
      title='Additional Information'
      content={
        isLoading ? (
          <Preloader
            size='sm'
            style={{ display: 'block', margin: '0 auto' }}
          />
        ) : (
          renderContent()
        )
      }
      actions={renderActions()}
      testPrefix='membership-view'
    />
  )
}
